import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, Loader2, Truck, Upload, Download, AlertTriangle } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none";

const CATEGORIES = [
  { key: "excavator", label: "Excavator", icon: "fa-tractor" },
  { key: "scaffold", label: "Scaffold", icon: "fa-layer-group" },
  { key: "lift", label: "Lift / Boom", icon: "fa-arrow-up" },
  { key: "truck", label: "Truck", icon: "fa-truck" },
  { key: "trailer", label: "Trailer", icon: "fa-trailer" },
  { key: "generator", label: "Generator", icon: "fa-plug" },
  { key: "tool", label: "Power Tool", icon: "fa-screwdriver-wrench" },
  { key: "other", label: "Other", icon: "fa-box" },
];

const STATUS_COLORS = {
  available: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rented: "bg-amber-100 text-amber-800 border-amber-200",
  maintenance: "bg-rose-100 text-rose-800 border-rose-200",
  retired: "bg-slate-100 text-slate-500 border-slate-200",
};

const empty = {
  name: "", category: "excavator", model: "", serial: "",
  ownership: "rented", supplier_name: "",
  daily_rate: 0, weekly_rate: 0, monthly_rate: 0,
  status: "available", notes: "",
  rental_start: "", rental_end: "", rental_contract_number: "", monthly_rental_cost: 0,
};

// Days until a date string (YYYY-MM-DD). Negative = past.
const daysUntil = (s) => {
  if (!s) return null;
  const d = new Date(s).getTime();
  if (isNaN(d)) return null;
  return Math.ceil((d - Date.now()) / 86400000);
};

const errMsg = (e, fallback = "Failed") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  return JSON.stringify(d);
};

const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function Equipment() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subs, setSubs] = useState([]);
  const [importing, setImporting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/equipment").then(setList).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/subcontractors").then(setSubs).catch(() => setSubs([]));
  }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...empty, ...e }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/equipment/${editing.id}`, form);
        toast.success("Equipment updated");
      } else {
        await api.post("/equipment", form);
        toast.success("Equipment added");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed to save")); }
    finally { setBusy(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.name}"?`)) return;
    try {
      await api.del(`/equipment/${e.id}`);
      toast.success("Deleted");
      load();    } catch (err) { toast.error(errMsg(err, "Failed to delete")); }
  };

  const filtered = list.filter((e) =>
    (filter === "all" || e.category === filter) &&
    (statusFilter === "all" || e.status === statusFilter)
  );
  const catCount = (k) => list.filter((e) => e.category === k).length;
  const statusCount = (k) => list.filter((e) => e.status === k).length;

  const downloadTemplate = () => api.downloadBlob("/equipment/import-template.xlsx", "equipment-import-template.xlsx").catch(() => toast.error("Failed to download template"));
  const onImportFile = async (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const r = await api.postFile("/equipment/import-excel", file);
      toast.success(`Imported ${r.imported}, skipped ${r.skipped}${r.errors?.length ? `, ${r.errors.length} errors` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Import failed")); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-4 p-6" data-testid="equipment-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Truck className="w-6 h-6" /> Equipment
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Company-owned and third-party rental equipment. Purchasing selects from here when a project needs equipment.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="equipment-download-template-btn"
            onClick={downloadTemplate}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg"
          ><Download className="w-3.5 h-3.5" /> Download template</button>
          <label className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg cursor-pointer">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Import Excel
            <input
              type="file" accept=".xlsx"
              onChange={onImportFile}
              disabled={importing}
              className="hidden"
              data-testid="equipment-import-input"
            />
          </label>
          <button
            data-testid="equipment-new-btn"
            onClick={openNew}
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-4 py-2 rounded-lg"
          ><Plus className="w-4 h-4" /> New equipment</button>
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase text-slate-500 mr-1">Category:</span>
          <button onClick={() => setFilter("all")} className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${filter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
            All ({list.length})
          </button>
          {CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => setFilter(c.key)}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${filter === c.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
              <i className={`fa-solid ${c.icon} mr-1`}></i> {c.label} ({catCount(c.key)})
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase text-slate-500 mr-1">Status:</span>
          <button onClick={() => setStatusFilter("all")} className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${statusFilter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>All</button>
          {["available", "rented", "maintenance", "retired"].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border capitalize ${statusFilter === s ? "bg-slate-900 text-white border-slate-900" : STATUS_COLORS[s]}`}>
              {s} ({statusCount(s)})
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-16 border-2 border-dashed border-slate-200 rounded-lg">
          {list.length === 0 ? "No equipment yet — click New equipment to add your first item." : "No equipment matches these filters."}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-500">
            <div className="col-span-3">Name / Model</div>
            <div className="col-span-1">Category</div>
            <div className="col-span-1">Ownership</div>
            <div className="col-span-2">Supplier</div>
            <div className="col-span-1 text-right">Daily</div>
            <div className="col-span-1 text-right">Weekly</div>
            <div className="col-span-1 text-right">Monthly</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {filtered.map((e) => {
            const cat = CATEGORIES.find((c) => c.key === e.category) || CATEGORIES[CATEGORIES.length - 1];
            return (
              <div key={e.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-100 text-xs hover:bg-slate-50/50" data-testid={`equipment-row-${e.id}`}>
                <div className="col-span-3 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{e.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">{e.model || "—"} · {e.serial || "no serial"}</div>
                </div>
                <div className="col-span-1 text-[10px] font-bold uppercase text-slate-500 truncate"><i className={`fa-solid ${cat.icon} mr-1`}></i> {cat.label}</div>
                <div className="col-span-1"><span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${e.ownership === "owned" ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-purple-50 text-purple-700 border border-purple-200"}`}>{e.ownership}</span></div>
                <div className="col-span-2 truncate text-slate-600">
                  {e.supplier_name || "—"}
                  {e.ownership === "rented" && e.rental_end && (() => {
                    const d = daysUntil(e.rental_end);
                    if (d === null) return null;
                    if (d < 0) return <div className="text-[10px] text-rose-600 font-bold inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> Overdue {Math.abs(d)}d</div>;
                    if (d < 7) return <div className="text-[10px] text-amber-600 font-bold inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> Expires in {d}d</div>;
                    return <div className="text-[10px] text-slate-400">Ends in {d}d</div>;
                  })()}
                </div>
                <div className="col-span-1 text-right tabular-nums">{money(e.daily_rate)}</div>
                <div className="col-span-1 text-right tabular-nums">{money(e.weekly_rate)}</div>
                <div className="col-span-1 text-right tabular-nums">{money(e.monthly_rate)}</div>
                <div className="col-span-1"><span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border capitalize ${STATUS_COLORS[e.status] || STATUS_COLORS.other}`}>{e.status}</span></div>
                <div className="col-span-1 text-right flex items-center justify-end gap-1">
                  <button
                    data-testid={`equipment-edit-${e.id}`}
                    onClick={() => openEdit(e)}
                    className="p-1 rounded hover:bg-slate-100 text-slate-600"><Pencil className="w-3.5 h-3.5" /></button>
                  <button
                    data-testid={`equipment-delete-${e.id}`}
                    onClick={() => remove(e)}
                    className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="equipment-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h2 className="text-base font-semibold inline-flex items-center gap-2">
                <Truck className="w-4 h-4" /> {editing ? "Edit equipment" : "New equipment"}
              </h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Name *</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="equipment-name" className={inputCls} placeholder="e.g. CAT 320 Excavator" /></div>
                <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Category</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select></div>
                <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Model</label>
                  <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className={inputCls} placeholder="e.g. 320GC" /></div>
                <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Serial #</label>
                  <input value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} className={inputCls} /></div>
                <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Ownership</label>
                  <select value={form.ownership} onChange={(e) => setForm({ ...form, ownership: e.target.value })} className={inputCls}>
                    <option value="owned">Owned by us</option>
                    <option value="rented">Rented from supplier</option>
                  </select></div>
                <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Status</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                    <option value="available">Available</option>
                    <option value="rented">Currently rented</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="retired">Retired</option>
                  </select></div>
                {form.ownership === "rented" && (
                  <div className="col-span-2 rounded-lg border border-purple-200 bg-purple-50/40 p-3 space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-purple-800">Rental details</div>
                    <div>
                      <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Supplier</label>
                      <input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Or pick from Subcontractors below" className={inputCls} />
                      {subs.length > 0 && (
                        <select value="" onChange={(e) => {
                          const s = subs.find((x) => x.id === e.target.value);
                          if (s) setForm({ ...form, supplier_id: s.id, supplier_name: s.name });
                        }} className={`${inputCls} mt-1`}>
                          <option value="">— or pick from CRM —</option>
                          {subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Rental start</label>
                        <input type="date" value={form.rental_start || ""} onChange={(e) => setForm({ ...form, rental_start: e.target.value })} className={inputCls} data-testid="equipment-rental-start" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Rental end</label>
                        <input type="date" value={form.rental_end || ""} onChange={(e) => setForm({ ...form, rental_end: e.target.value })} className={inputCls} data-testid="equipment-rental-end" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Contract / PO #</label>
                        <input value={form.rental_contract_number || ""} onChange={(e) => setForm({ ...form, rental_contract_number: e.target.value })} className={inputCls} data-testid="equipment-rental-contract" placeholder="PO-4471" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Monthly rental cost ($)</label>
                        <input type="number" step="0.01" value={form.monthly_rental_cost || 0} onChange={(e) => setForm({ ...form, monthly_rental_cost: parseFloat(e.target.value) || 0 })} className={`${inputCls} text-right tabular-nums`} data-testid="equipment-rental-cost" />
                      </div>
                    </div>
                  </div>
                )}
                <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Daily rate ($)</label>
                  <input type="number" step="0.01" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: parseFloat(e.target.value) || 0 })} className={`${inputCls} text-right tabular-nums`} /></div>
                <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Weekly rate ($)</label>
                  <input type="number" step="0.01" value={form.weekly_rate} onChange={(e) => setForm({ ...form, weekly_rate: parseFloat(e.target.value) || 0 })} className={`${inputCls} text-right tabular-nums`} /></div>
                <div className="col-span-2"><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Monthly rate ($)</label>
                  <input type="number" step="0.01" value={form.monthly_rate} onChange={(e) => setForm({ ...form, monthly_rate: parseFloat(e.target.value) || 0 })} className={`${inputCls} text-right tabular-nums`} /></div>
                <div className="col-span-2"><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Notes</label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="2" className={inputCls} /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white">
              <button onClick={() => setOpen(false)} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
              <button
                data-testid="equipment-save-btn"
                onClick={save} disabled={busy}
                className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                {editing ? "Save changes" : "Create equipment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
